import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Factory, Cpu, Recycle, Settings, CheckCircle, TrendingUp, Award, Shield, Zap, Target } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Icon mapping
const iconMap = {
  Factory, Cpu, Recycle, Settings, CheckCircle, TrendingUp, Award, Shield, Zap, Target
};

export default function ManufacturingPage() {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/page/manufacturing`);
      setContent(response.data);
    } catch (error) {
      console.error("Error fetching manufacturing content:", error);
      // Use default content on error
      setContent(getDefaultContent());
    } finally {
      setLoading(false);
    }
  };

  const getDefaultContent = () => ({
    hero_title: "Manufacturing Excellence",
    hero_subtitle: "World-class infrastructure, German automation, and ISO-certified processes powering India's premium POSM manufacturing.",
    hero_image: "https://images.unsplash.com/photo-1720036237334-9263cd28c3d4?w=1600",
    capabilities_title: "Our Capabilities",
    capabilities_subtitle: "Advanced infrastructure built for scale and precision",
    capabilities: [
      { icon: "Factory", title: "42,000+ Sq. Ft. Facility", description: "State-of-the-art manufacturing unit designed for high-volume production with dedicated zones for metal work, assembly, quality control, and packaging." },
      { icon: "Cpu", title: "10+ German Automated Machines", description: "Advanced CNC machines, laser cutters, and automated welding systems ensuring precision, consistency, and rapid turnaround times." },
      { icon: "Recycle", title: "Eco-Friendly Processes", description: "Sustainable manufacturing with recyclable materials, UV-cured inks, and minimal waste generation. Aluminum-based foldable displays reduce logistics costs by up to 60%." },
      { icon: "Settings", title: "ISO-Certified Quality", description: "Rigorous quality control at every stage - from raw material inspection to final product testing. ISO-certified processes meeting international standards." }
    ],
    process_title: "Our Process",
    process_subtitle: "From concept to delivery - structured workflow ensuring quality at every step",
    process_steps: [
      { step: "01", title: "Requirement Analysis", description: "Understanding client needs, product specifications, and deployment environment" },
      { step: "02", title: "3D Design & Approval", description: "Creating detailed CAD models and prototypes for client approval" },
      { step: "03", title: "Material Sourcing", description: "Procuring high-quality raw materials from trusted suppliers" },
      { step: "04", title: "Precision Manufacturing", description: "CNC cutting, welding, and assembly using automated machinery" },
      { step: "05", title: "Quality Inspection", description: "Multi-point quality checks ensuring dimensional accuracy and finish" },
      { step: "06", title: "Finishing & Packaging", description: "Powder coating, branding application, and secure packaging for transit" }
    ],
    materials_title: "Premium Materials",
    materials_subtitle: "High-quality raw materials ensuring durability and aesthetics",
    materials: [
      { name: "Mild Steel", use: "Heavy-duty displays and structural components" },
      { name: "Galvanized Iron", use: "Corrosion-resistant outdoor displays" },
      { name: "Aluminum", use: "Lightweight, foldable, and recyclable solutions" },
      { name: "Bubble Guard", use: "Protective layers and impact resistance" },
      { name: "MDF", use: "Finished surfaces and branding panels" },
      { name: "Powder Coating", use: "Durable, colorful finishes" }
    ],
    cta_title: "Ready to Experience Quality?",
    cta_subtitle: "Schedule a facility tour or request a sample to see our manufacturing excellence firsthand.",
    cta_button_text: "Contact Manufacturing Team",
    cta_email: "sales@hokico.in"
  });

  if (loading) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-xl">Loading...</div>
        </div>
        <Footer />
      </div>
    );
  }

  const heroImage = content.hero_image || "https://images.unsplash.com/photo-1720036237334-9263cd28c3d4?w=1600";

  return (
    <div className="min-h-screen">
      <Navbar />

      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center min-h-[60vh] flex items-center"
        style={{
          backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.85), rgba(15, 23, 42, 0.85)), url('${heroImage}')`
        }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-20">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-6xl font-bold tracking-tight mb-6 text-white" style={{ fontFamily: 'Manrope' }} data-testid="manufacturing-heading">
              {content.hero_title}
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              {content.hero_subtitle}
            </p>
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Manrope' }}>{content.capabilities_title}</h2>
            <p className="text-lg text-muted-foreground">{content.capabilities_subtitle}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {content.capabilities.map((capability, index) => {
              const IconComponent = iconMap[capability.icon] || Settings;
              return (
                <div key={index} className="p-8 border border-border/50 rounded-sm bg-white hover:border-primary/50 transition-colors">
                  <IconComponent className="h-12 w-12 text-primary mb-4" />
                  <h3 className="text-2xl font-bold mb-3" style={{ fontFamily: 'Manrope' }}>{capability.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{capability.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Manufacturing Process */}
      <section className="py-20 bg-muted/20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Manrope' }}>{content.process_title}</h2>
            <p className="text-lg text-muted-foreground">{content.process_subtitle}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {content.process_steps.map((item, index) => (
              <div key={index} className="relative p-6 bg-white border border-border/50 rounded-sm">
                <div className="text-6xl font-bold text-primary/10 absolute top-4 right-4" style={{ fontFamily: 'Manrope' }}>
                  {item.step}
                </div>
                <div className="relative z-10">
                  <h3 className="text-xl font-bold mb-3" style={{ fontFamily: 'Manrope' }}>{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Materials */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Manrope' }}>{content.materials_title}</h2>
            <p className="text-lg text-muted-foreground">{content.materials_subtitle}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {content.materials.map((material, index) => (
              <div key={index} className="p-6 border border-border/50 rounded-sm bg-white">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-lg font-bold mb-2" style={{ fontFamily: 'Manrope' }}>{material.name}</h3>
                    <p className="text-sm text-muted-foreground">{material.use}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-secondary text-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <TrendingUp className="h-16 w-16 mx-auto mb-6" />
          <h2 className="text-4xl font-bold tracking-tight mb-6" style={{ fontFamily: 'Manrope' }}>{content.cta_title}</h2>
          <p className="text-xl mb-8">
            {content.cta_subtitle}
          </p>
          <a href={`mailto:${content.cta_email}`}>
            <button className="px-8 py-3 bg-white text-secondary font-semibold rounded-sm hover:bg-gray-100 transition-colors">
              {content.cta_button_text}
            </button>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
